import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Flex,
  Link,
  Stack,
  useToast,
  Text,
  Button,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { userPermissions } from "@/contexts/userPermissions";
import { PermissionEnum } from "@/utils/permissionEnum";
import { CiReceipt, CiSearch } from "react-icons/ci";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import {
  getTypeRouters,
  updateTypeRouterResponse,
} from "@/services/consignado/config-engine.service";

// Interface baseada no seu JSON Mock
interface TypeRouter {
  id: number;
  nome: string;
  descricao: string;
  ativo: boolean;
  criado_em: string;
}

// Interface auxiliar para simular a estrutura paginada que o front já esperava
interface PaginatedResponse<T> {
  itens: T[];
  total_paginas: number;
  pagina_atual: number;
  pagina_anterior?: number;
  proxima_pagina?: number;
}

export default function ConsignadoEngineConfigTable() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPage, setTotalPage] = useState(1);
  const { user } = useEnsureAuthenticated();
  const permissions = userPermissions(user);
  const toast = useToast();
  const queryCache = useQueryClient();
  const backgroundStyle = useConditionalBackground();

  // Ajustado para os campos novos (ativo ao invés de status)
  const [sortBy, setSortBy] = useState<"id" | "nome" | "descricao" | "ativo">(
    "id"
  );
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");

  const [search, setSearch] = useState("");

  const handleSort = async (field: "id" | "nome" | "descricao" | "ativo") => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "ASC" ? "DESC" : "ASC"
    );
  };

  const { data } = useQuery<PaginatedResponse<TypeRouter>>({
    queryKey: ["type-routers", page, pageSize, sortBy, sortDirection], // Chave alterada
    queryFn: async () => {
      // Busca os dados brutos do seu mock
      const rawData = await getTypeRouters();

      // LOGICA DE MOCK DE PAGINAÇÃO (Client-Side)
      // Como o mock retorna um array puro, simulamos a estrutura paginada aqui
      // para não quebrar a lógica visual da tabela.
      const totalItems = rawData.length;
      const totalPages = Math.ceil(totalItems / pageSize) || 1;

      // Simples ordenação client-side para o mock funcionar visualmente
      const sortedData = [...rawData].sort((a, b) => {
        if (a[sortBy] < b[sortBy]) return sortDirection === "ASC" ? -1 : 1;
        if (a[sortBy] > b[sortBy]) return sortDirection === "ASC" ? 1 : -1;
        return 0;
      });

      const startIndex = (page - 1) * pageSize;
      const paginatedItems = sortedData.slice(
        startIndex,
        startIndex + pageSize
      );

      return {
        itens: paginatedItems,
        total_paginas: totalPages,
        pagina_atual: page,
        pagina_anterior: page > 1 ? page - 1 : undefined,
        proxima_pagina: page < totalPages ? page + 1 : undefined,
      };
    },
    staleTime: 100 * 1000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (data?.total_paginas) {
      setTotalPage(data.total_paginas);
    }
  }, [data]);

  const handleDeactivateTypeRouter = useMutation({
    mutationFn: async (vars: { id: number; ativo: boolean }) => {
      // TODO: Substituir pela chamada real de API quando existir (ex: updateTypeRouter)
      await updateTypeRouterResponse(vars.id, { ativo: vars.ativo });
      // Simulando delay de API
      // await new Promise(resolve => setTimeout(resolve, 500));
      return vars;
    },
    onSuccess(_, variables) {
      toast({
        title: "Sucesso",
        description: `Rota ${variables.ativo ? "ativada" : "desativada"} com sucesso `,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Atualiza o cache localmente para refletir a mudança na tela sem refresh
      const cachedData = queryCache.getQueryData<PaginatedResponse<TypeRouter>>(
        ["type-routers", page, pageSize, sortBy, sortDirection]
      );

      if (cachedData) {
        const updatedItems = cachedData.itens.map((item) =>
          item.id === variables.id ? { ...item, ativo: variables.ativo } : item
        );

        queryCache.setQueryData(
          ["type-routers", page, pageSize, sortBy, sortDirection],
          { ...cachedData, itens: updatedItems }
        );
      }
    },
    onError() {
      toast({
        title: "Erro",
        description: "Erro ao atualizar status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
      >
        <Flex
          alignItems={{ base: "center", md: "center" }}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiReceipt size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Tipos de Rotas
            </Text>
          </Flex>

          <Flex
            flexDirection={{ base: "column", md: "row" }}
            alignItems="flex-end"
            mt={{ base: 4, md: 0 }}
            width="100%"
            justifyContent={{ base: "center", md: "flex-end" }}
            gap={4}
          >
            {permissions.some(
              (permission) => permission === PermissionEnum.CREATE_USER
            ) && (
              <Stack
                direction={{ base: "column", md: "row" }}
                spacing={4}
                width="100%"
                justifyContent={{ base: "center", md: "end" }}
              >
                <Link
                  as={RouterLink}
                  to="novo"
                  bg="white"
                  color="black"
                  px={4}
                  py={{ base: 4, md: 2 }}
                  borderRadius="md"
                  fontWeight="bold"
                  _hover={{ bg: "gray.200" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: "lg", md: "md" }}
                  height={{ base: "56px", md: "40px" }}
                  width={{ base: "100%", md: "auto" }}
                  fontFamily="Lexend Deca"
                >
                  <CiReceipt style={{ marginRight: "8px", fontSize: "20px" }} />
                  Cadastrar Tipo
                </Link>
              </Stack>
            )}
          </Flex>
        </Flex>
        <Flex w="30%" mt={8} gap={2}>
          <InputGroup>
            <InputLeftElement mt={1} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              height={{ base: "56px", md: "48px" }}
              bg={"white"}
              placeholder="Pesquisar..."
              variant="filled"
              type="text"
              maxLength={30}
              color="black"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <Flex
        justify="center"
        alignItems="center"
        mb={4}
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
      >
        <Flex alignItems="center" mb={{ base: 2, md: 0 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage(data?.pagina_anterior || 1)}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {data?.pagina_atual || page} de {totalPage}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage(data?.proxima_pagina || 1)}
            isDisabled={data?.pagina_atual === data?.total_paginas}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mt={{ base: 2, md: 0 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>

      <Box width="100%" overflow="visible">
        <TableContainer borderRadius="md" boxShadow="md">
          <Table size="sm" w="100%">
            <Thead
              {...backgroundStyle}
              position="sticky"
              top={0}
              zIndex={1}
              boxShadow="md"
            >
              <Tr>
                <Th color={"white"} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>AÇÕES</Text>
                  </HStack>
                </Th>
                <Th color={"white"} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Id</Text>
                    <IconButton
                      icon={
                        sortBy === "id" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Id"
                      bg={"gray.200"}
                      onClick={() => handleSort("id")}
                    />
                  </HStack>
                </Th>
                <Th color={"white"} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>NOME</Text>
                    <IconButton
                      icon={
                        sortBy === "nome" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Name"
                      bg={"gray.200"}
                      onClick={() => handleSort("nome")}
                    />
                  </HStack>
                </Th>

                <Th color={"white"} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Descrição</Text>
                    <IconButton
                      icon={
                        sortBy === "descricao" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Descricao"
                      bg={"gray.200"}
                      onClick={() => handleSort("descricao")}
                    />
                  </HStack>
                </Th>

                <Th color={"white"} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>ATIVO</Text>
                    <IconButton
                      icon={
                        sortBy === "ativo" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by active"
                      bg={"gray.200"}
                      onClick={() => handleSort("ativo")}
                    />
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data?.itens?.map((item) => (
                <Tr
                  key={item.id}
                  transition="all 0.3s ease-in-out"
                  borderBottom="1px solid"
                  fontFamily="Lexend Deca"
                >
                  <Td
                    textAlign="center"
                    fontSize={{ base: "lg", md: "sm" }}
                    py={{ base: 2, md: 3 }}
                    fontFamily="Lexend Deca"
                    width={"250px"}
                  >
                    <Flex justify="center" flexDirection="column" gap={2}>
                      {item.ativo ? (
                        <Button
                          size={{ base: "md", md: "md" }}
                          colorScheme="red"
                          onClick={() =>
                            handleDeactivateTypeRouter.mutate({
                              id: item.id,
                              ativo: false,
                            })
                          }
                          width="100%"
                          py={{ base: 2.5, md: 2 }}
                          fontSize={{ base: "md", md: "sm" }}
                          fontWeight="bold"
                          fontFamily="Lexend Deca"
                        >
                          Desativar
                        </Button>
                      ) : (
                        <Button
                          size={{ base: "md", md: "md" }}
                          colorScheme="green"
                          onClick={() =>
                            handleDeactivateTypeRouter.mutate({
                              id: item.id,
                              ativo: true,
                            })
                          }
                          width="100%"
                          py={{ base: 2.5, md: 2 }}
                          fontSize={{ base: "md", md: "sm" }}
                          fontWeight="bold"
                          fontFamily="Lexend Deca"
                        >
                          Ativar
                        </Button>
                      )}
                    </Flex>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    <Link
                      as={RouterLink}
                      to={`/consignado/engine-config/${item.id}`} // Ajuste a rota conforme necessário
                      color="blue.500"
                    >
                      {item.id}
                    </Link>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    <Text whiteSpace="normal" wordBreak="break-word">
                      {item.nome}
                    </Text>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    <Text whiteSpace="normal" wordBreak="break-word">
                      {item.descricao}
                    </Text>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {item.ativo ? "Sim" : "Não"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage(data?.pagina_anterior || 1)}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {data?.pagina_atual || page} de {totalPage}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage(data?.proxima_pagina || 1)}
            isDisabled={data?.pagina_atual === data?.total_paginas}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
    </Box>
  );
}
